import { StyleSheet, TextStyle } from 'react-native';

/**
 * One markdown-display style entry: a RN TextStyle plus the web-only
 * `overflowWrap` (RN 0.73's TextStyle omits it, but RN-web's Text honors it —
 * see the `text` entry below). Typing the return keeps every widened literal
 * (`fontWeight: '700'` etc.) inside TextStyle's unions, which is what the
 * Markdown component's `StyleSheet.NamedStyles<any>` prop demands.
 */
type MarkdownStyle = TextStyle & { overflowWrap?: 'anywhere' | 'break-word' };

/** Map theme tokens → react-native-markdown-display style sheet. */
export function createMarkdownStyles(theme: any, color: string): Record<string, MarkdownStyle> {
  const fs = theme.typography.fontSizeMd;
  const mono = theme.colors.surface;
  const rule = theme.colors.border || '#e0e0e0'; // Use existing border token instead of non-existent 'muted'
  return {
    body: { color, fontSize: fs },
    // A bare URL or long identifier is unbreakable, and this bubble is
    // shrink-to-fit, so the token's width becomes the bubble's min-content width
    // and paints past the available space. `anywhere` is the value that lowers
    // min-content width; `break-word` does not. This has to sit on `text` rather
    // than an ancestor: the library renders leaf text through a `<Text>` that
    // already declares `break-word` on web, which beats inheritance, and
    // `overflowWrap` is not in its inheritable textStyleProps list.
    text: { overflowWrap: 'anywhere' },
    paragraph: { marginTop: 0, marginBottom: 8, color },
    heading1: { color, fontSize: fs * 1.5, fontWeight: '700', marginTop: 8, marginBottom: 4 },
    heading2: { color, fontSize: fs * 1.3, fontWeight: '700', marginTop: 8, marginBottom: 4 },
    heading3: { color, fontSize: fs * 1.15, fontWeight: '700', marginTop: 6, marginBottom: 2 },
    heading4: { color, fontSize: fs, fontWeight: '700', marginTop: 4, marginBottom: 2 },
    strong: { fontWeight: '700' },
    em: { fontStyle: 'italic' },
    link: { color: theme.colors.primary, textDecorationLine: 'underline' },
    bullet_list: { marginVertical: 4 },
    ordered_list: { marginVertical: 4 },
    list_item: { color, marginVertical: 1 },
    code_inline: {
      backgroundColor: mono,
      color,
      fontFamily: 'monospace',
      paddingHorizontal: 4,
      borderRadius: 3,
    },
    code_block: {
      backgroundColor: mono,
      color,
      fontFamily: 'monospace',
      padding: 8,
      borderRadius: 6,
      marginVertical: 4,
    },
    fence: {
      backgroundColor: mono,
      color,
      fontFamily: 'monospace',
      padding: 8,
      borderRadius: 6,
      marginVertical: 4,
    },
    blockquote: {
      backgroundColor: mono,
      borderLeftColor: theme.colors.primary,
      borderLeftWidth: 3,
      paddingHorizontal: 8,
      paddingVertical: 4,
      marginVertical: 4,
    },
    table: { borderColor: rule, borderWidth: 1, borderRadius: 4, marginVertical: 4 },
    thead: { backgroundColor: mono },
    th: { padding: 6, borderColor: rule, color, fontWeight: '700' },
    td: { padding: 6, borderColor: rule, color },
    hr: { backgroundColor: rule, height: StyleSheet.hairlineWidth, marginVertical: 8 },
  };
}
